import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronDown, ExternalLink, Loader2, Target } from 'lucide-react';
import LoggedInUserWrapper from '../core/LoggedInUserWrapper';
import { Card } from '../ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useStudentPerformance } from '../../queries';
import { getAssignmentEndUrl } from '../../utils';

const DURATIONS = ['Last 30 days', 'Last 60 days', 'Last 90 days', 'This semester'];
const DURATION_PARAM = {
  'Last 30 days': '30',
  'Last 60 days': '60',
  'Last 90 days': '90',
  'This semester': 'semester'
};
const ALL_SUBJECTS = 'All subjects';

function shortLabel(label) {
  const text = String(label || '');
  return text.length > 16 ? `${text.slice(0, 14)}…` : text;
}

function classComparison(overall, classAverage) {
  if (overall == null || classAverage == null) return 'After your teacher publishes results';
  const delta = classAverage - overall;
  if (delta === 0) return 'In line with class avg';
  if (delta > 0) return `${delta}% below class avg`;
  return `${Math.abs(delta)}% above class avg`;
}

function RadarChartSVG({ data }) {
  const cx = 140;
  const cy = 140;
  const maxR = 90;
  const labelR = maxR + 26;
  const n = data.length;
  const rings = [0.25, 0.5, 0.75, 1];

  const angle = (index) => (index / n) * 2 * Math.PI - Math.PI / 2;
  const point = (index, radius) => ({
    x: cx + radius * Math.cos(angle(index)),
    y: cy + radius * Math.sin(angle(index))
  });

  const polyPoints = (values) =>
    values
      .map((value, index) => {
        const { x, y } = point(index, (value / 100) * maxR);
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(' ');

  const ringPoints = (factor) =>
    Array.from({ length: n }, (_, index) => {
      const { x, y } = point(index, factor * maxR);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    }).join(' ');

  const textAnchor = (index) => {
    const x = point(index, labelR).x;
    if (x > cx + 6) return 'start';
    if (x < cx - 6) return 'end';
    return 'middle';
  };

  return (
    <svg viewBox="0 0 280 280" width="100%" className="max-h-[260px]" aria-hidden>
      {rings.map((factor, ringIndex) => (
        <polygon
          key={`ring-${ringIndex}`}
          points={ringPoints(factor)}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="1"
        />
      ))}
      {Array.from({ length: n }, (_, index) => {
        const { x, y } = point(index, maxR);
        return (
          <line
            key={`spoke-${index}`}
            x1={cx}
            y1={cy}
            x2={x.toFixed(2)}
            y2={y.toFixed(2)}
            stroke="#E5E7EB"
            strokeWidth="1"
          />
        );
      })}
      <polygon
        points={polyPoints(data.map((item) => item.classAvg))}
        fill="#3B82F6"
        fillOpacity="0.12"
        stroke="#3B82F6"
        strokeWidth="1.5"
      />
      <polygon
        points={polyPoints(data.map((item) => item.student))}
        fill="#16A34A"
        fillOpacity="0.2"
        stroke="#16A34A"
        strokeWidth="2"
      />
      {data.map((item, index) => {
        const { x, y } = point(index, labelR);
        return (
          <text
            key={`label-${index}`}
            x={x.toFixed(2)}
            y={y.toFixed(2)}
            fontSize="9.5"
            fill="#6B7280"
            textAnchor={textAnchor(index)}
            dominantBaseline="middle"
          >
            {shortLabel(item.subject)}
          </text>
        );
      })}
    </svg>
  );
}

function LineChartSVG({ data }) {
  const width = 400;
  const height = 220;
  const left = 36;
  const right = 392;
  const top = 10;
  const bottom = 185;
  const chartHeight = bottom - top;
  const chartWidth = right - left;
  const gridValues = [0, 25, 50, 75, 100];

  const getX = (index) =>
    data.length <= 1 ? left + chartWidth / 2 : left + (index / (data.length - 1)) * chartWidth;
  const getY = (value) => bottom - (value / 100) * chartHeight;
  const path = (key) =>
    data.map(
      (point, index) => `${index === 0 ? 'M' : 'L'} ${getX(index).toFixed(1)} ${getY(point[key]).toFixed(1)}`
    ).join(' ');

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" className="max-h-[240px]" aria-hidden>
      {gridValues.map((value) => (
        <line
          key={`hgrid-${value}`}
          x1={left}
          y1={getY(value).toFixed(1)}
          x2={right}
          y2={getY(value).toFixed(1)}
          stroke="#E5E7EB"
          strokeWidth="0.5"
          strokeDasharray="3 3"
        />
      ))}
      {gridValues.map((value) => (
        <text
          key={`ylabel-${value}`}
          x={left - 5}
          y={getY(value).toFixed(1)}
          fontSize="10"
          fill="#9CA3AF"
          textAnchor="end"
          dominantBaseline="middle"
        >
          {value}
        </text>
      ))}
      <line x1={left} y1={bottom} x2={right} y2={bottom} stroke="#9CA3AF" strokeWidth="0.5" />
      {data.map((point, index) => (
        <text
          key={`xlabel-${index}`}
          x={getX(index).toFixed(1)}
          y={bottom + 14}
          fontSize="11"
          fill="#6B7280"
          textAnchor="middle"
        >
          {point.month}
        </text>
      ))}
      <path d={path('classAvg')} fill="none" stroke="#3B82F6" strokeWidth="2" strokeDasharray="5 4" />
      <path d={path('you')} fill="none" stroke="#16A34A" strokeWidth="2.5" />
      {data.map((point, index) => (
        <circle
          key={`dot-avg-${index}`}
          cx={getX(index).toFixed(1)}
          cy={getY(point.classAvg).toFixed(1)}
          r="3.5"
          fill="#3B82F6"
        />
      ))}
      {data.map((point, index) => (
        <circle
          key={`dot-you-${index}`}
          cx={getX(index).toFixed(1)}
          cy={getY(point.you).toFixed(1)}
          r="4"
          fill="#16A34A"
        />
      ))}
    </svg>
  );
}

function ScoreBadge({ score }) {
  if (score == null || Number.isNaN(Number(score))) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const color =
    score >= 70
      ? 'bg-green-100 text-green-700 border-green-200'
      : score >= 40
        ? 'bg-amber-100 text-amber-700 border-amber-200'
        : 'bg-red-100 text-red-700 border-red-200';

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${color}`}>
      {score}%
    </span>
  );
}

function StatCard({ label, icon: Icon, iconColor, borderColor, bgColor, children }) {
  return (
    <div className={`flex flex-col gap-3 rounded-xl border p-4 ${borderColor} ${bgColor}`}>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 flex-shrink-0 ${iconColor}`} strokeWidth={2} />
        <span className="text-xs leading-tight font-medium text-muted-foreground">{label}</span>
      </div>
      {children}
    </div>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  return (
    <fieldset className="relative min-w-0 rounded-md border border-border px-0 pt-0 pb-0">
      <legend className="ml-2 px-1 text-[10px] leading-none font-medium text-muted-foreground">{label}</legend>
      <DropdownMenu>
        <DropdownMenuTrigger className="-mt-1 flex w-full min-w-[7.5rem] max-w-[11rem] cursor-pointer items-center justify-between gap-2 rounded-b-md bg-white py-1.5 pr-2 pl-2.5 text-xs font-medium text-foreground transition-colors hover:bg-muted/20 focus:outline-none">
          <span className="truncate">{value}</span>
          <ChevronDown className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-64 min-w-[10rem] overflow-y-auto">
          {options.map((option) => (
            <DropdownMenuItem
              key={option}
              onSelect={() => onChange(option)}
              className={option === value ? 'bg-muted/70 text-xs' : 'text-xs'}
            >
              <span className="truncate">{option}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </fieldset>
  );
}

function assessmentResultUrl(assessment) {
  if (!assessment?.studentAssignmentId) return null;
  return getAssignmentEndUrl({
    studentAssignmentId: assessment.studentAssignmentId,
    showResult: true
  });
}

function AssessmentName({ assessment }) {
  const href = assessmentResultUrl(assessment);
  const name = (
    <span className="min-w-0 max-w-xs truncate text-sm font-medium text-foreground">{assessment.name}</span>
  );
  if (!href) return name;
  return (
    <Link to={href} className="flex min-w-0 items-center gap-2 text-foreground hover:underline">
      {name}
      <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
    </Link>
  );
}

function ChartSelect({ value, options, onChange }) {
  const current = options.find((option) => option.id === value) || options[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex max-w-[9.5rem] items-center gap-1 rounded-md border border-border bg-white px-2.5 py-1.5 text-xs text-foreground transition-colors hover:bg-muted/50">
        <span className="truncate">{current?.label || 'Overall'}</span>
        <ChevronDown className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-64 w-44 overflow-y-auto">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.id}
            onSelect={() => onChange(option.id)}
            className={option.id === current?.id ? 'bg-muted/70 text-xs' : 'text-xs'}
          >
            <span className="truncate">{option.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EmptyChart({ children }) {
  return <p className="px-4 py-16 text-center text-xs text-muted-foreground">{children}</p>;
}

function CompetencyList({ data }) {
  return (
    <ul className="space-y-3 py-4">
      {data.map((item) => (
        <li key={item.subject} className="flex items-center justify-between gap-3 text-sm">
          <span className="min-w-0 truncate text-foreground">{item.subject}</span>
          <span className="flex-shrink-0 text-xs text-muted-foreground">
            <span className="font-semibold text-green-700">{item.student}%</span>
            <span> · class {item.classAvg}%</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function PerformancePage() {
  const [duration, setDuration] = useState(DURATIONS[0]);
  const [subject, setSubject] = useState(ALL_SUBJECTS);
  const [competencyViewId, setCompetencyViewId] = useState('chapter');
  const [trendSeriesId, setTrendSeriesId] = useState('overall');
  const querySubject = subject === ALL_SUBJECTS ? '' : subject;
  const { data, isLoading, isError, refetch } = useStudentPerformance({
    duration: DURATION_PARAM[duration],
    subject: querySubject
  });

  const subjects = data?.subjects || [];
  const subjectOptions = [ALL_SUBJECTS, ...subjects];

  useEffect(() => {
    const available = data?.subjects || [];
    if (subject !== ALL_SUBJECTS && data && !available.includes(subject)) {
      setSubject(ALL_SUBJECTS);
    }
  }, [data, subject]);

  const summary = data?.summary || {};
  const competencyViews = data?.competencyViews?.length
    ? data.competencyViews
    : [{
      id: 'chapter',
      label: 'Chapters',
      subtitle: 'Chapter-wise proficiency',
      items: data?.competency || [],
      trends: []
    }];
  const activeView = competencyViews.find((view) => view.id === competencyViewId) || competencyViews[0];
  const competency = activeView?.items || [];
  const trendOptions = [
    { id: 'overall', label: 'Overall', points: data?.trend || [] },
    ...(activeView?.trends || [])
  ];
  const activeTrend = trendOptions.find((option) => option.id === trendSeriesId) || trendOptions[0];
  const trend = activeTrend?.points || [];
  const recentAssessments = data?.recentAssessments || [];
  const classLabel = data?.student?.classLabel;
  const subjectLabel = subject === ALL_SUBJECTS && subjects.length === 1 ? subjects[0] : subject;
  const subtitle = [subjectLabel, classLabel, duration].filter(Boolean).join(' · ');
  const showLoader = isLoading && !data;

  return (
    <LoggedInUserWrapper>
      <div className="min-h-0 flex-1 overflow-y-auto bg-white">
        <div className="space-y-5 p-4 pb-8 md:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-lg font-bold text-foreground">My Performance</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <FilterSelect label="Duration" value={duration} options={DURATIONS} onChange={setDuration} />
              <FilterSelect label="Subject" value={subject} options={subjectOptions} onChange={setSubject} />
            </div>
          </div>

          {showLoader ? (
            <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading performance
            </div>
          ) : null}

          {isError ? (
            <div className="rounded-xl border border-border px-4 py-10 text-center">
              <p className="text-sm text-foreground">Couldn&apos;t load performance.</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-3 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/50"
              >
                Try again
              </button>
            </div>
          ) : null}

          {!showLoader && !isError ? (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatCard
                  label="Assessments Completed"
                  icon={CheckCircle2}
                  iconColor="text-purple-500"
                  borderColor="border-purple-200"
                  bgColor="bg-purple-50/60"
                >
                  <div>
                    <p className="text-3xl leading-none font-bold text-purple-600">
                      {summary.assessmentsCompleted ?? 0}
                    </p>
                    <p className="mt-1.5 text-xs text-muted-foreground">{duration}</p>
                  </div>
                </StatCard>

                <StatCard
                  label="Overall Performance"
                  icon={Target}
                  iconColor="text-blue-500"
                  borderColor="border-blue-200"
                  bgColor="bg-blue-50/60"
                >
                  <div>
                    <p className="text-3xl leading-none font-bold text-blue-600">
                      {summary.overall == null ? '—' : `${summary.overall}%`}
                    </p>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {classComparison(summary.overall, summary.classAverage)}
                    </p>
                  </div>
                </StatCard>

                <StatCard
                  label="Strongest Chapter"
                  icon={CheckCircle2}
                  iconColor="text-green-500"
                  borderColor="border-green-200"
                  bgColor="bg-green-50/60"
                >
                  <div>
                    <p className="line-clamp-2 text-base leading-snug font-bold text-green-600">
                      {summary.strongest?.name || '—'}
                    </p>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {summary.strongest ? `${summary.strongest.percent}% score` : 'No chapter scores yet'}
                    </p>
                  </div>
                </StatCard>

                <StatCard
                  label="Needs Attention"
                  icon={AlertTriangle}
                  iconColor="text-red-500"
                  borderColor="border-red-200"
                  bgColor="bg-red-50/60"
                >
                  <div>
                    <p className="line-clamp-2 text-base leading-snug font-bold text-red-600">
                      {summary.needsAttention?.name || '—'}
                    </p>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {summary.needsAttention ? `${summary.needsAttention.percent}% score` : 'No chapter to flag'}
                    </p>
                  </div>
                </StatCard>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Card className="p-4 md:p-5">
                  <div className="mb-3 flex items-start justify-between">
                    <div>
                      <h2 className="text-sm font-semibold text-foreground">Competency Map</h2>
                      <p className="text-xs text-muted-foreground">
                        {activeView?.subtitle || 'Chapter-wise proficiency'}
                      </p>
                    </div>
                    <ChartSelect
                      value={activeView?.id}
                      options={competencyViews}
                      onChange={(id) => {
                        setCompetencyViewId(id);
                        setTrendSeriesId('overall');
                      }}
                    />
                  </div>
                  {competency.length >= 3 ? <RadarChartSVG data={competency} /> : null}
                  {competency.length > 0 && competency.length < 3 ? <CompetencyList data={competency} /> : null}
                  {competency.length === 0 ? (
                    <EmptyChart>Chapter scores show up here after your teacher publishes a result.</EmptyChart>
                  ) : null}
                  <div className="mt-2 flex items-center justify-center gap-5">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block h-0.5 w-4 rounded-full bg-green-600" />
                      <span className="text-xs text-muted-foreground">You</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block h-0.5 w-4 rounded-full bg-blue-500" />
                      <span className="text-xs text-muted-foreground">Class avg</span>
                    </div>
                  </div>
                </Card>

                <Card className="p-4 md:p-5">
                  <div className="mb-3 flex items-start justify-between">
                    <div>
                      <h2 className="text-sm font-semibold text-foreground">Performance Trend</h2>
                      <p className="text-xs text-muted-foreground">Progress over the semester</p>
                    </div>
                    <ChartSelect
                      value={activeTrend?.id}
                      options={trendOptions}
                      onChange={setTrendSeriesId}
                    />
                  </div>
                  {trend.length > 0 ? <LineChartSVG data={trend} /> : (
                    <EmptyChart>Your score trend shows up here after a result is published.</EmptyChart>
                  )}
                  <div className="mt-2 flex items-center justify-center gap-5">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block h-0.5 w-4 rounded-full bg-green-600" />
                      <span className="text-xs text-muted-foreground">You</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <svg width="16" height="4" className="inline-block" aria-hidden>
                        <line x1="0" y1="2" x2="16" y2="2" stroke="#3B82F6" strokeWidth="2" strokeDasharray="4 3" />
                      </svg>
                      <span className="text-xs text-muted-foreground">Class avg</span>
                    </div>
                  </div>
                </Card>
              </div>

              <Card className="overflow-hidden">
                <div className="border-b border-border/60 px-4 py-4 md:px-5">
                  <h2 className="text-sm font-semibold text-foreground">Recent Assessments</h2>
                  <p className="mt-0.5 text-xs text-muted-foreground">Your submission history · {duration}</p>
                </div>

                {recentAssessments.length === 0 ? (
                  <p className="px-5 py-8 text-center text-sm text-muted-foreground">
                    Published results will show up here once your teacher publishes them.
                  </p>
                ) : (
                  <>
                    <div className="hidden md:block">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border/40 bg-muted/30">
                            <th className="px-5 py-2.5 text-left text-xs font-medium text-muted-foreground">Assessment</th>
                            <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Date</th>
                            <th className="px-4 py-2.5 text-center text-xs font-medium text-muted-foreground">Score</th>
                            <th className="px-4 py-2.5 text-center text-xs font-medium text-muted-foreground">Class Avg</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentAssessments.map((assessment) => (
                            <tr
                              key={assessment.studentAssignmentId || assessment.name}
                              className="border-b border-border/30 transition-colors last:border-0 hover:bg-muted/20"
                            >
                              <td className="px-5 py-3">
                                <AssessmentName assessment={assessment} />
                              </td>
                              <td className="px-4 py-3 text-xs whitespace-nowrap text-muted-foreground">{assessment.date}</td>
                              <td className="px-4 py-3 text-center">
                                <ScoreBadge score={assessment.score} />
                              </td>
                              <td className="px-4 py-3 text-center">
                                <ScoreBadge score={assessment.classAvg} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="divide-y divide-border/40 md:hidden">
                      {recentAssessments.map((assessment) => (
                        <div key={assessment.studentAssignmentId || assessment.name} className="flex items-center gap-3 px-4 py-3">
                          <div className="min-w-0 flex-1">
                            <AssessmentName assessment={assessment} />
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {assessment.date} · Class avg {assessment.classAvg}%
                            </p>
                          </div>
                          <ScoreBadge score={assessment.score} />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Card>
            </>
          ) : null}
        </div>
      </div>
    </LoggedInUserWrapper>
  );
}

export default PerformancePage;
